import { useCallback, useEffect, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ProjectOverviewButton } from "@/components/ProjectOverviewButton";
import { isElectronWithOpenFolder } from "@/lib/electron";
import { ApiError } from "@/lib/apiClient";
import {
  getOrderChangeLog,
  updateOrderLineLot,
  type ChangeLogEntry,
  type OrderHistoryLine,
  type OrderHistoryRow,
} from "@/lib/orders";
import { orderLinePlainText, orderTextWithArticleNumber } from "@/lib/orderDraftUtils";
import { formatRequestAge } from "@/pages/material/maListDisplay";
import {
  deliveryLabel,
  fmtOrderDate,
  fmtOrderDateTime,
  splitBestellnummern,
  submissionChannelLabel,
} from "@/pages/bestellungen/orderHistoryDisplay";
import { OrderSourceLinks } from "@/pages/bestellungen/OrderSourceLinks";
import { OrderWerkszeugnisseSection } from "@/pages/bestellungen/OrderWerkszeugnisseSection";
import { WerkszeugnisBadge } from "@/pages/bestellungen/OrdersHistorySection";
import type { InvoiceMailOrderHint } from "@/lib/invoiceMail";

function formatLogSnapshot(snapshot?: ChangeLogEntry["before_snapshot"]): string {
  if (snapshot?.geliefert_at !== undefined && !snapshot?.lines) {
    return snapshot.geliefert_at
      ? `Geliefert am ${fmtOrderDateTime(snapshot.geliefert_at)}`
      : "Nicht als geliefert markiert";
  }
  const lines = Array.isArray(snapshot?.lines) ? snapshot.lines : [];
  if (!lines.length) return "Keine Positionen";
  return lines
    .map((ln, idx) => {
      const qty = ln.menge ?? "—";
      const unit = (ln.einheit || "").trim();
      const text =
        orderLinePlainText(orderTextWithArticleNumber(ln.text || "", ln.hero_nr)) || "—";
      const bn = (ln.bestellnummer || "").trim();
      const auftrag = (ln.auftragsnummer || "").trim();
      const lot = (ln.lot_nummer || "").trim();
      return `${idx + 1}. ${qty}${unit ? ` ${unit}` : ""} - ${text}${auftrag ? ` [Auftrag ${auftrag}]` : ""}${bn ? ` [TK ${bn}]` : ""}${lot ? ` [LOT ${lot}]` : ""}`;
    })
    .join("\n");
}

function logEntryLabel(entry: ChangeLogEntry): string {
  switch (entry.action) {
    case "delivery_marked":
      return "Als geliefert markiert";
    case "delivery_unmarked":
      return "Liefer-Markierung zurückgenommen";
    case "lot_updated":
      return "LOT-Nummer geändert";
    case "update": {
      const before = formatLogSnapshot(entry.before_snapshot);
      const after = formatLogSnapshot(entry.after_snapshot);
      return before !== after ? "Bestellung bearbeitet" : "Bestellung unverändert gespeichert";
    }
    default:
      return entry.action || "Änderung";
  }
}

export interface OrderDetailDialogProps {
  open: boolean;
  loading?: boolean;
  order: OrderHistoryRow | null;
  canMarkDelivered: boolean;
  onClose: () => void;
  onReorderAll: (row: OrderHistoryRow) => void;
  onReorderOne: (row: OrderHistoryRow, index: number) => void;
  onEdit: (row: OrderHistoryRow) => void;
  onRequestCancel: (row: OrderHistoryRow) => void;
  onRequestRestore: (row: OrderHistoryRow) => void;
  onMarkDelivered: (row: OrderHistoryRow) => Promise<void>;
  onUnmarkDelivered: (row: OrderHistoryRow) => Promise<void>;
  invoiceHint?: InvoiceMailOrderHint;
  onViewInvoice?: () => void;
  onOrderChange: (order: OrderHistoryRow) => void;
  zIndexClass?: string;
}

export function OrderDetailDialog({
  open,
  loading = false,
  order,
  canMarkDelivered,
  onClose,
  onReorderAll,
  onReorderOne,
  onEdit,
  onRequestCancel,
  onRequestRestore,
  onMarkDelivered,
  onUnmarkDelivered,
  invoiceHint,
  onViewInvoice,
  onOrderChange,
  zIndexClass = "z-[110]",
}: OrderDetailDialogProps) {
  const [logRows, setLogRows] = useState<ChangeLogEntry[]>([]);
  const [logLoading, setLogLoading] = useState(false);
  const [deliveryBusy, setDeliveryBusy] = useState(false);

  const reloadLog = useCallback(async (orderId: number) => {
    setLogLoading(true);
    try {
      const list = await getOrderChangeLog(orderId);
      setLogRows(Array.isArray(list) ? list.slice(0, 30) : []);
    } catch {
      setLogRows([]);
    } finally {
      setLogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !order?.id) {
      setLogRows([]);
      return;
    }
    void reloadLog(order.id);
  }, [open, order?.id, reloadLog]);

  if (!open) return null;

  if (loading && !order) {
    return (
      <Dialog open onClose={onClose} title="Bestellung" zIndexClass={zIndexClass}>
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </Dialog>
    );
  }

  if (!order) return null;

  const row = order;

  const cancelled = Boolean(row.cancelled_at);
  const delivered = Boolean(row.geliefert_at);
  const items = Array.isArray(row.positionen) ? row.positionen : [];
  const projects = Array.isArray(row.projects) ? row.projects : [];
  const firstProject = projects.find((p) => Number(p.auftrag_id) > 0);
  const electron = isElectronWithOpenFolder();
  const tkList = splitBestellnummern(row.bestellnummern);
  const contact = (row.supplier_contact_name || "").trim();
  const channelLabel = submissionChannelLabel(row.submission_channel);
  const lieferLabel = deliveryLabel(row);
  const hasInvoice = (invoiceHint?.mail_count ?? 0) > 0;
  const invoicePending = Boolean(invoiceHint?.has_pending);

  async function handleMarkDelivered() {
    setDeliveryBusy(true);
    try {
      await onMarkDelivered(row);
      await reloadLog(row.id);
    } finally {
      setDeliveryBusy(false);
    }
  }

  async function handleUnmarkDelivered() {
    setDeliveryBusy(true);
    try {
      await onUnmarkDelivered(row);
      await reloadLog(row.id);
    } finally {
      setDeliveryBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        <span className="flex flex-wrap items-center gap-2">
          {tkList.length ? (
            tkList.map((tk) => (
              <span key={tk} className="font-mono text-sm font-semibold">{tk}</span>
            ))
          ) : (
            <span>Bestellung #{row.id}</span>
          )}
          {delivered ? (
            <span className="rounded-full border border-status-green/40 bg-status-green/15 px-2.5 py-0.5 text-xs font-semibold text-status-green">
              Geliefert · {fmtOrderDate(row.geliefert_at)}
            </span>
          ) : null}
          {cancelled ? (
            <span className="rounded-full border border-status-red/30 bg-status-red/15 px-2.5 py-0.5 text-xs font-semibold text-status-red">
              Storniert
            </span>
          ) : null}
          {Number(row.werkszeugnis_count || 0) > 0 ? <WerkszeugnisBadge /> : null}
        </span>
      }
      size="2xl"
      zIndexClass={zIndexClass}
      fullScreenMobile
      bodyClassName="space-y-4"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <ProjectOverviewButton auftragId={firstProject?.auftrag_id} />
          {electron && firstProject?.ordnerpfad ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                const path = (firstProject.ordnerpfad || "").trim();
                if (path && window.envAPI?.openFolder) void window.envAPI.openFolder(path);
              }}
            >
              Ordner
            </Button>
          ) : null}
          <Button type="button" size="sm" variant="secondary" onClick={() => onReorderAll(row)}>
            Alle erneut bestellen
          </Button>
          {!cancelled ? (
            <Button type="button" size="sm" variant="primary" onClick={() => onEdit(row)}>
              Bearbeiten
            </Button>
          ) : null}
          {!cancelled ? (
            <Button type="button" size="sm" variant="secondary" onClick={() => onRequestCancel(row)}>
              Stornieren
            </Button>
          ) : (
            <Button type="button" size="sm" variant="secondary" onClick={() => onRequestRestore(row)}>
              Stornierung aufheben
            </Button>
          )}
          {canMarkDelivered && !cancelled && !delivered ? (
            <Button
              type="button"
              size="sm"
              variant="primary"
              loading={deliveryBusy}
              onClick={() => void handleMarkDelivered()}
            >
              Als geliefert markieren
            </Button>
          ) : null}
          {canMarkDelivered && delivered ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              loading={deliveryBusy}
              onClick={() => void handleUnmarkDelivered()}
            >
              Lieferung zurücknehmen
            </Button>
          ) : null}
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    >
      <dl className="grid gap-3 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-xs text-label-secondary">Lieferant</dt>
          <dd className="font-semibold text-label">{row.supplier_name || "—"}</dd>
        </div>
        <div>
          <dt className="text-xs text-label-secondary">Ansprechpartner</dt>
          <dd className="text-label">{contact || "—"}</dd>
        </div>
        <div>
          <dt className="text-xs text-label-secondary">Bestellt am</dt>
          <dd className="text-label">
            {fmtOrderDateTime(row.created_at)}
            <span className="ml-2 text-xs font-semibold text-status-orange">
              ({formatRequestAge(row.created_at)})
            </span>
          </dd>
        </div>
        <div>
          <dt className="text-xs text-label-secondary">Lieferung</dt>
          <dd className="text-label">{lieferLabel || "—"}</dd>
        </div>
        {channelLabel ? (
          <div>
            <dt className="text-xs text-label-secondary">Versandweg</dt>
            <dd className="text-label">{channelLabel}</dd>
          </div>
        ) : null}
        {delivered ? (
          <div>
            <dt className="text-xs text-label-secondary">Liefertag</dt>
            <dd className="font-semibold text-status-green">{fmtOrderDateTime(row.geliefert_at)}</dd>
          </div>
        ) : null}
      </dl>

      {hasInvoice ? (
        <section className="rounded-ios border border-separator bg-fill-quaternary/20 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-label">Rechnung</h3>
              <p className="text-xs text-label-secondary">
                {invoiceHint?.mail_count === 1
                  ? "1 Rechnungs-Mail zugeordnet"
                  : `${invoiceHint?.mail_count ?? 0} Rechnungs-Mails zugeordnet`}
                {invoicePending ? " · noch nicht abgelegt" : ""}
              </p>
            </div>
            {onViewInvoice ? (
              <Button type="button" size="sm" variant="secondary" onClick={onViewInvoice}>
                Rechnung ansehen
              </Button>
            ) : null}
          </div>
        </section>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <OrderSourceLinks order={row} />
      </div>

      {projects.length > 0 ? (
        <section>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-label-secondary">
            Aufträge
          </h3>
          <ul className="space-y-1 text-sm text-label">
            {projects.map((p) => {
              const line = [p.auftragsnummer, p.ort, p.beschreibung].filter(Boolean).join(" · ");
              if (!line) return null;
              return <li key={p.auftrag_id}>{line}</li>;
            })}
          </ul>
        </section>
      ) : null}

      <section>
        <h3 className="mb-2 text-sm font-semibold text-label">Positionen ({items.length})</h3>
        {items.length === 0 ? (
          <p className="text-sm text-label-tertiary">Keine Artikel hinterlegt.</p>
        ) : (
          <ul className="divide-y divide-separator rounded-ios border border-separator">
            {items.map((line, idx) => (
              <OrderLineDetail
                key={line.id ?? `${row.id}-${idx}`}
                orderId={row.id}
                line={line}
                onReorder={() => onReorderOne(row, idx)}
                onLotSaved={(lotNummer) => {
                  const positionen = items.map((item, itemIdx) =>
                    (line.id != null ? item.id === line.id : itemIdx === idx)
                      ? { ...item, lot_nummer: lotNummer }
                      : item,
                  );
                  onOrderChange({ ...row, positionen });
                  void reloadLog(row.id);
                }}
              />
            ))}
          </ul>
        )}
      </section>

      <OrderWerkszeugnisseSection order={row} onOrderChange={onOrderChange} />

      <section>
        <h3 className="mb-2 text-sm font-semibold text-label">Änderungslog</h3>
        {logLoading ? (
          <p className="flex items-center gap-2 text-sm text-label-secondary">
            <Spinner /> Lade …
          </p>
        ) : logRows.length === 0 ? (
          <p className="text-sm text-label-tertiary">Noch keine Einträge.</p>
        ) : (
          <ul className="space-y-2 max-h-64 overflow-y-auto">
            {logRows.map((entry, idx) => (
              <li
                key={`${entry.changed_at}-${idx}`}
                className="rounded-ios border border-separator bg-card-elevated p-2 text-xs"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold text-label">{logEntryLabel(entry)}</span>
                  <time className="text-label-tertiary">{fmtOrderDateTime(entry.changed_at)}</time>
                </div>
                <p className="mt-1 text-label-secondary">{entry.changed_by || "—"}</p>
                {entry.action === "delivery_marked" || entry.action === "delivery_unmarked" ? (
                  <p className="mt-1 text-label-secondary">
                    {formatLogSnapshot(entry.before_snapshot)} → {formatLogSnapshot(entry.after_snapshot)}
                  </p>
                ) : null}
                {entry.action === "lot_updated" ? (
                  <p className="mt-1 text-label-secondary">
                    {(entry.before_snapshot?.lot_nummer || "—")} → {(entry.after_snapshot?.lot_nummer || "—")}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </Dialog>
  );
}

function OrderLineDetail({
  orderId,
  line,
  onReorder,
  onLotSaved,
}: {
  orderId: number;
  line: OrderHistoryLine;
  onReorder: () => void;
  onLotSaved: (lotNummer: string | null) => void;
}) {
  const name =
    orderLinePlainText(orderTextWithArticleNumber(line.text || "", line.hero_nr)) || "—";
  const [lot, setLot] = useState(line.lot_nummer || "");
  const [saving, setSaving] = useState(false);
  const [lotError, setLotError] = useState<string | null>(null);

  useEffect(() => {
    setLot(line.lot_nummer || "");
  }, [line.id, line.lot_nummer]);

  async function saveLot() {
    if (line.id == null) return;
    const next = lot.trim();
    if (next === (line.lot_nummer || "").trim()) return;
    setSaving(true);
    setLotError(null);
    try {
      const saved = await updateOrderLineLot(orderId, line.id, next || null);
      onLotSaved(saved.lot_nummer ?? null);
    } catch (err) {
      setLotError(err instanceof ApiError ? err.message : "LOT-Nummer konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <li className="flex flex-wrap items-center gap-3 px-3 py-2">
      <span className="font-semibold tabular-nums text-label">{line.menge ?? "—"}</span>
      <span className="text-sm text-label-secondary">{(line.einheit || "").trim() || "—"}</span>
      <span className="min-w-0 flex-1 text-sm text-label">{name || "—"}</span>
      <label className="text-xs text-label-secondary">
        LOT-Nr.
        <input
          className="mt-0.5 block w-36 rounded-md border border-separator bg-card px-2 py-1 text-sm text-label"
          value={lot}
          maxLength={80}
          disabled={saving || line.id == null}
          title="LOT-Nummer des Artikels, damit das Werkszeugnis später zugeordnet werden kann."
          onChange={(e) => setLot(e.target.value)}
          onBlur={() => void saveLot()}
        />
      </label>
      {lotError ? <span className="text-xs text-status-red">{lotError}</span> : null}
      <Button type="button" size="sm" variant="secondary" onClick={onReorder}>
        Erneut bestellen
      </Button>
    </li>
  );
}
